import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  BoldIcon,
  ItalicIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { cleanPastedHtml } from '@/lib/paste-clean';

export interface RichTextEditorProps {
  /** Called with the editor's HTML on every change ('' when empty). */
  readonly onChange: (html: string) => void;
  /** Initial HTML content. Read once at mount. */
  readonly initialHtml?: string;
  /** Placeholder-ish label for accessibility. */
  readonly ariaLabel?: string;
}

/**
 * The note composer's rich-text editor: bold / italic / underline / strike,
 * lists and links — the same vocabulary the legacy Slate editor produced, so
 * everything written here renders through the existing NoteBody allowlist.
 *
 * Emits HTML. An empty document emits '' so "is there a body?" stays a simple
 * length check for the caller.
 *
 * @param props - Change handler, optional initial HTML and aria label.
 * @returns The editor with its toolbar.
 */
export function RichTextEditor({
  onChange,
  initialHtml,
  ariaLabel,
}: RichTextEditorProps): React.ReactElement {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5] },
        link: {
          openOnClick: false,
          // New-tab links with a safe rel; TipTap validates the scheme.
          HTMLAttributes: { target: '_blank', rel: 'noreferrer noopener' },
        },
      }),
    ],
    content: initialHtml ?? '',
    editorProps: {
      // Fathom summaries paste as one link per line; Word and Docs bring
      // inline styles. Clean before the schema sees it.
      transformPastedHTML: cleanPastedHtml,
      attributes: {
        // A comfortable writing surface; note-prose matches the NoteBody
        // viewer. Long notes scroll inside so the dialog stays on screen.
        class:
          'note-prose min-h-[160px] max-h-[55vh] overflow-y-auto px-3 py-2 text-portal-body focus:outline-none',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
    onUpdate: ({ editor: current }) => {
      onChange(current.isEmpty ? '' : current.getHTML());
    },
  });

  if (!editor) return <div className="min-h-[200px]" />;

  /** Prompts for a URL and links the selection (or unlinks on empty). */
  function setLink(): void {
    if (!editor) return;
    const previous = editor.getAttributes('link')['href'] as string | undefined;
    // A prompt beats building a popover for an internal tool; Esc cancels.
    const url = window.prompt('Link URL', previous ?? 'https://');
    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().setLink({ href: url.trim() }).run();
  }

  const buttons: Array<{
    label: string;
    icon: React.ReactNode;
    active: boolean;
    onClick: () => void;
  }> = [
    {
      label: 'Bold',
      icon: <BoldIcon aria-hidden className="size-4" />,
      active: editor.isActive('bold'),
      onClick: () => editor.chain().focus().toggleBold().run(),
    },
    {
      label: 'Italic',
      icon: <ItalicIcon aria-hidden className="size-4" />,
      active: editor.isActive('italic'),
      onClick: () => editor.chain().focus().toggleItalic().run(),
    },
    {
      label: 'Underline',
      icon: <UnderlineIcon aria-hidden className="size-4" />,
      active: editor.isActive('underline'),
      onClick: () => editor.chain().focus().toggleUnderline().run(),
    },
    {
      label: 'Strikethrough',
      icon: <StrikethroughIcon aria-hidden className="size-4" />,
      active: editor.isActive('strike'),
      onClick: () => editor.chain().focus().toggleStrike().run(),
    },
    {
      label: 'Bulleted list',
      icon: <ListIcon aria-hidden className="size-4" />,
      active: editor.isActive('bulletList'),
      onClick: () => editor.chain().focus().toggleBulletList().run(),
    },
    {
      label: 'Numbered list',
      icon: <ListOrderedIcon aria-hidden className="size-4" />,
      active: editor.isActive('orderedList'),
      onClick: () => editor.chain().focus().toggleOrderedList().run(),
    },
    {
      label: 'Link',
      icon: <LinkIcon aria-hidden className="size-4" />,
      active: editor.isActive('link'),
      onClick: setLink,
    },
  ];

  return (
    <div className="rounded-portal-input border border-[#cad3df] bg-white focus-within:border-brand">
      <div
        role="toolbar"
        aria-label="Text formatting"
        className="flex flex-wrap items-center gap-0.5 border-b border-portal-line px-1.5 py-1"
      >
        {buttons.map((button) => (
          <button
            key={button.label}
            type="button"
            aria-label={button.label}
            aria-pressed={button.active}
            onMouseDown={(event) => event.preventDefault()}
            onClick={button.onClick}
            className={cn(
              'grid size-7 cursor-pointer place-items-center rounded-md',
              button.active
                ? 'bg-brand/10 text-brand'
                : 'text-portal-muted hover:bg-admin-subtle hover:text-portal-ink',
            )}
          >
            {button.icon}
          </button>
        ))}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
